// Secrets (02-DESIGN/02-secrets/01-the-vault.md § subjects and operations, §
// access; 01-the-console.md § the words: *Secrets*, *a secret* and its
// *versions*): the workspace vault's paths and versions; *Store a secret*, a
// form in the page's place (01-the-console.md § conventions: a form), the
// value typed once and never shown again; delete the latest
// version or remove every one, neither reversible. A value is never
// displayed: the admin template admits store, delete and list and never a
// read, so the console cannot show one. The workspace's vault is placed at
// its creation (decision 0021): while nothing serves the family yet, the page
// shows the vault's status as it shows any workload's and asks again until
// the vault answers (§ 6), never waiting on it.

import { vault } from "@impire-io/sdk";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import {
  Button,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  TextInput,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { listSubject, PlatformName, SERVICE as WORKLOADS, type ListReply } from "../../wire/workloads.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Table } from "../../ui/table.tsx";
import { useSurface, type Surface } from "../surface.tsx";
import { plainState, platformWorkload } from "../workloads/state.ts";

/** POLL is how often the page asks again whether the vault answers while it starts, in milliseconds. */
export const POLL = 4000;

/**
 * listPaths lists the vault's paths under a prefix. The page asks for every
 * path, the empty prefix, and its table's filter narrows them in the browser
 * (01-the-console.md § conventions: a table).
 */
function listPaths(bus: Surface["bus"], prefix: string): Promise<vault.ListReply> {
  return bus.vault().list(prefix);
}

export function SecretsPage() {
  const s = useSurface();
  const [tick, setTick] = useState(0);
  const [marked, setMarked] = useState<string | undefined>(undefined);
  const [list, reload] = useLoad("vault", () => listPaths(s.bus, ""), tick, {
    keep: true,
  });
  const form = useFormState<"store">();
  const [err, setErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);

  const remove = (path: string, all: boolean) => {
    setErr(undefined);
    s.bus
      .vault()
      .delete(path, all)
      .then(
        (reply) => {
          setNote(
            all
              ? `Every version of ${reply.path} is gone (${reply.removed}).`
              : `The latest version of ${reply.path} is gone; earlier ones stay readable by number until they roll out.`,
          );
          reload();
        },
        (e: unknown) => {
          setErr(e);
        },
      );
  };

  const noVault = list.state === "refused" && isRefusal(list.err, CODE_NO_SERVICE);
  const [workloads] = useLoad(
    `vault/workloads/${noVault ? "none" : "some"}`,
    () =>
      noVault
        ? s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined)
        : Promise.resolve<ListReply>({ workloads: [] }),
    tick,
    { keep: true },
  );
  const service =
    workloads.state === "ready" ? platformWorkload(workloads.value.workloads, PlatformName.vault) : undefined;

  // While nothing serves the vault, ask again until it answers.
  useEffect(() => {
    if (!noVault) {
      return;
    }
    const t = setTimeout(() => {
      setTick((n) => n + 1);
    }, POLL);
    return () => {
      clearTimeout(t);
    };
  }, [noVault, tick]);
  const store = (
    <Button
      kind="primary"
      onClick={() => {
        form.show("store");
      }}
      disabled={noVault}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Store a secret
    </Button>
  );

  if (form.open === "store") {
    return (
      <StoreSecret
        onClose={form.close}
        onStored={(reply) => {
          form.close();
          setMarked(reply.path);
          setNote(
            `Stored ${reply.path} as version ${reply.version}. The value cannot be read back from here.`,
          );
          reload();
        }}
      />
    );
  }

  return (
    <>
      <PageHead title="Secrets" line={`What ${s.name}'s workloads read, and nobody sees.`} action={store} />
      {err !== undefined && <NotAllowed err={err} />}
      {note !== undefined && (
        <Notice kind="ok">
          {note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}

      {list.state === "pending" && <Pending what="Reading the secrets" />}
      {noVault && service !== undefined && (
        <Notice title="The vault is not answering yet">
          It runs as a workload of {s.name}&apos;s own,{" "}
          <Link className="text-link" to={tenantPath(s.tenant, "workloads", service.id)}>
            {service.name}
          </Link>
          , now <Pill tone={plainState(service).tone}>{plainState(service).word}</Pill>. Storing a secret is
          offered the moment it answers.
        </Notice>
      )}
      {noVault && service === undefined && workloads.state !== "pending" && (
        <Notice title="The vault is not running">
          {s.name}&apos;s vault is placed when the workspace is created, and it is not among {s.name}&apos;s
          workloads yet. Storing a secret is offered the moment it answers.
        </Notice>
      )}
      {list.state === "refused" && !noVault && <NotAllowed err={list.err} what="Secrets" />}
      {list.state === "ready" && list.value.paths.length === 0 && (
        <Empty action={store}>
          No secrets yet. A workload reads its own at workload.&lt;id&gt;.&lt;name&gt;.
        </Empty>
      )}
      {list.state === "ready" && list.value.paths.length > 0 && (
        <Table
          label="Secrets"
          rows={list.value.paths}
          rowKey={(p) => p.path}
          sort={{ key: "path", dir: "asc" }}
          mark={marked}
          columns={[
            {
              key: "path",
              header: "Secret",
              width: "minmax(0, 2fr)",
              value: (p) => p.path,
              cell: (p) => <span className="mono">{p.path}</span>,
            },
            {
              key: "version",
              header: "Version",
              width: "90px",
              value: (p) => p.version,
              cell: (p) => <span className="muted">{p.version}</span>,
            },
            {
              key: "stored",
              header: "Stored",
              width: "170px",
              narrow: false,
              value: (p) => p.stored_at,
              text: (p) => when(p.stored_at),
              cell: (p) => <span className="small muted">{when(p.stored_at)}</span>,
            },
          ]}
          menu={(p) => [
            {
              label: "Delete latest",
              confirm: {
                title: `Delete the latest version of ${p.path}?`,
                consequence:
                  "Not reversible: the latest version is gone. Earlier ones stay until they roll out.",
              },
              onSelect: () => {
                remove(p.path, false);
              },
            },
            {
              label: "Remove",
              danger: true,
              confirm: {
                title: `Remove secret ${p.path}?`,
                consequence: "Not reversible: every version is gone.",
              },
              onSelect: () => {
                remove(p.path, true);
              },
            },
          ]}
        />
      )}
    </>
  );
}

function StoreSecret({
  onClose,
  onStored,
}: {
  onClose: () => void;
  onStored: (reply: vault.StoreReply) => void;
}) {
  const s = useSurface();
  const [path, setPath] = useState("");
  const [value, setValue] = useState("");
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const trimmed = path.trim();
  const pathOK = trimmed !== "" && vault.validPath(trimmed);
  const size = new TextEncoder().encode(value).length;
  const tooBig = size > vault.MAX_VALUE;
  const ready = pathOK && value !== "" && !tooBig;
  const submit = () => {
    if (busy || !ready) {
      return;
    }
    setErr(undefined);
    const expectedVersion = expected.trim() === "" ? undefined : Number(expected.trim());
    setBusy(true);
    s.bus
      .vault()
      .store(trimmed, value, expectedVersion)
      .then(
        (reply) => {
          setBusy(false);
          setValue("");
          onStored(reply);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      title="Store a secret"
      line="Readable by the workload it is for, and by nothing else: not by you, and not by this page."
      back="Secrets"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready}>
            {busy ? "Storing…" : "Store"}
          </Button>
        </>
      }
    >
      {err !== undefined && <NotAllowed err={err} what="Store" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          label="Secret"
          hint={
            pathOK || trimmed === ""
              ? "Its path: dot-separated names. A workload reads its own at workload.<id>.<name>."
              : "Each name between the dots: no space, dot, * or > inside one."
          }
        >
          <TextInput
            value={path}
            onChange={setPath}
            placeholder="workload.<id>.api-key"
            mono
            required
            autoFocus
          />
        </Field>
        <Field
          label="Value"
          hint={
            tooBig
              ? `${size} bytes: over the 64 KiB bound.`
              : "Typed once; never shown again, not even to you. Up to 64 KiB."
          }
        >
          <TextInput value={value} onChange={setValue} type="password" required />
        </Field>
        <Field label="Expected version" hint="Optional: store only if the current version is this one.">
          <TextInput value={expected} onChange={setExpected} type="number" />
        </Field>
      </form>
    </FormPage>
  );
}
